import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Manager } from '@/services/actualDataService';
import { Users } from 'lucide-react';
import React from 'react';

export interface Employee {
	id: string;
	name?: string | null;
	email?: string | null;
	department?: string | null;
	position?: string | null;
	managerId?: string | null;
	managerName?: string | null;
}

export interface ChangePreview {
	employee: Manager;
	currentManager: string;
	newManager: string;
	willChange: boolean;
}

interface EmployeeListProps {
	employees?: Manager[];
	changes?: ChangePreview[];
	title: string;
	showChanges?: boolean;
}

const EmployeeList: React.FC<EmployeeListProps> = ({
	employees = [],
	changes = [],
	title,
	showChanges = false,
}) => {
	const displayData = showChanges
		? changes
		: employees.map((emp) => ({ employee: emp, willChange: false }));

	if (!displayData || displayData.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Users className="h-5 w-5" />
						{title}
					</CardTitle>
				</CardHeader>
				<CardContent>
					<p className="text-gray-500 text-center py-4">
						No employees found
					</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<Users className="h-5 w-5" />
					{title} ({displayData.length} employees)
				</CardTitle>
			</CardHeader>
			<CardContent>
				<div className="space-y-3">
					{displayData.map((item) => {
						const employee =
							'employee' in item ? item.employee : item;
						const willChange =
							'willChange' in item ? item.willChange : false;

						return (
							<div
								key={employee.id}
								className={`p-3 rounded-lg border ${
									willChange
										? 'bg-yellow-50 border-yellow-200'
										: 'bg-white border-gray-200'
								}`}
							>
								<div className="flex justify-between items-start">
									<div className="flex-1">
										<div className="flex items-center gap-2 mb-1">
											<h4 className="font-medium">
												{employee.name}
											</h4>
											{willChange && (
												<Badge
													variant="outline"
													className="text-orange-600 border-orange-600"
												>
													Will Change
												</Badge>
											)}
										</div>
										<p className="text-sm text-gray-600">
											{employee.displayName} -{' '}
											{employee.email}
										</p>
										{showChanges &&
										'currentManager' in item &&
										'newManager' in item ? (
											<div className="mt-2 text-sm">
												<p className="text-red-600">
													From:{' '}
													{String(
														item.currentManager,
													)}
												</p>
												<p className="text-green-600">
													To:{' '}
													{String(item.newManager)}
												</p>
											</div>
										) : (
											<p className="text-sm text-blue-600 mt-1">
												Manager:{' '}
												{employee.manager?.displayName}{' '}
												{' - '}{' '}
												{employee?.manager?.email}
											</p>
										)}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</CardContent>
		</Card>
	);
};

export default EmployeeList;
